import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Save, ShieldAlert, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/** Admin-only page for rotating Twilio credentials without a re-publish.
 *  DB-first: notifications.py reads `app_settings.twilio_config` (this record)
 *  before falling back to env vars. */
export default function TwilioConfigAdmin() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/twilio-config");
      setInfo(data);
      setForm((f) => ({ ...f, from_number: data.from_number || "" }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.account_sid.trim() || form.account_sid.trim().length < 8) return toast.error("Account SID looks too short.");
    if (!form.auth_token.trim()  || form.auth_token.trim().length < 8)  return toast.error("Auth token looks too short.");
    if (!form.from_number.trim()) return toast.error("From number is required.");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/twilio-config", {
        account_sid: form.account_sid.trim(),
        auth_token:  form.auth_token.trim(),
        from_number: form.from_number.trim(),
      });
      toast.success(`Saved — active source is now "${data.active.source}"`);
      setForm({ ...form, account_sid: "", auth_token: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    const to = (testTo || "").trim();
    if (!/^\+?\d{7,}$/.test(to.replace(/[\s()-]/g, ""))) return toast.error("Enter a valid E.164 phone (e.g. +15625551234).");
    setTesting(true);
    try {
      const { data } = await api.post("/admin/twilio-config/test", { to });
      toast.success(`Sent ✓ (sid ${data.sid?.slice(0, 10)}… · ${data.status})`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="twilio-config-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-neutral-900">Twilio credentials</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-xl leading-relaxed">
          Rotate the Twilio Account SID + Auth Token + FROM number here. Changes save to the database and take
          effect immediately — no re-publish. DB values override the deployed integration.
        </p>
      </div>

      <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="twilio-active-card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Currently active</div>
          <button onClick={load} disabled={loading} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1" data-testid="twilio-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : info ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <StatLine label="Source"       value={info.source === "db" ? "🟢 DB override" : "🟡 Env fallback"} />
            <StatLine label="Configured"   value={info.configured ? "Yes" : "No"} />
            <StatLine label="From number"  value={info.from_number || "—"} mono />
            <StatLine label="SID suffix"   value={info.sid_last4 ? `…${info.sid_last4}` : "—"} mono />
          </div>
        ) : null}
      </div>

      <form onSubmit={save} className="bg-white border border-neutral-200 p-6 mb-6" data-testid="twilio-form">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Update credentials</div>
        <Field label="Account SID">
          <input type="password" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="ACXXXXXXXXXX…" className="lf-input font-mono w-full" data-testid="twilio-input-sid" autoComplete="off" />
        </Field>
        <Field label="Auth token">
          <input type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder="XXXXXXXXXX…" className="lf-input font-mono w-full" data-testid="twilio-input-token" autoComplete="off" />
        </Field>
        <Field label="From number (E.164)">
          <input type="text" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} placeholder="+15625551234" className="lf-input font-mono w-full" data-testid="twilio-input-from" />
        </Field>
        <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-3 mb-4">
          <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div>Sending SMS to US recipients requires A2P 10DLC brand + campaign registration on your Twilio account. Otherwise carriers will silently block delivery.</div>
        </div>
        <button type="submit" disabled={saving} className="lf-btn-primary inline-flex items-center gap-2" data-testid="twilio-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save credentials"}
        </button>
      </form>

      <div className="bg-white border border-neutral-200 p-6" data-testid="twilio-test-card">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Send a test SMS</div>
        <div className="flex gap-3">
          <input type="tel" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+15625551234" className="lf-input flex-1 font-mono" data-testid="twilio-test-to" />
          <button onClick={test} disabled={testing || !info?.configured} className="lf-btn-outline inline-flex items-center gap-2 whitespace-nowrap" data-testid="twilio-test-btn">
            <Send className="w-4 h-4" /> {testing ? "Sending…" : "Send test SMS"}
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-3">Uses the currently-active configuration. A successful send confirms Twilio auth + FROM number are working.</p>
      </div>
    </div>
  );
}

function StatLine({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm text-neutral-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
